import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Star,
  ArrowRight,
  Calendar,
  Euro,
  Home,
  Anchor,
  Fish,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const MarkenPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: Home,
      title: t("1597", "Traditional Houses"),
      description: t(
        "1635",
        "Iconic green wooden houses on stilts with unique architecture"
      ),
    },
    {
      icon: Anchor,
      title: t("1636", "Island Village"),
      description: t(
        "1637",
        "Explore this charming former island connected by causeway"
      ),
    },
    {
      icon: Fish,
      title: t("1638", "Fishing Heritage"),
      description: t(
        "1639",
        "Learn about traditional Dutch fishing culture and history"
      ),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t(
        "1640",
        "Capture the picturesque harbor and traditional costumes"
      ),
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1641", "Village exploration opportunities"),
    t("1642", "Harbor area visits"),
    t("1643", "Cultural site viewing"),
    t("1644", "Traditional architecture tours"),
    t("1489", "Photography opportunities"),
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1645", "Half Day (4-5 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1646", "€120 per person"),
      icon: Euro,
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Marken.webp"
            alt={t("1647", "Marken Traditional Village")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Anchor className="h-4 w-4" />
              <span>{t("1648", "Traditional Dutch Village")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1649", "Historic")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1650", "Marken")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1651",
                "A charming island village with traditional wooden houses and rich fishing heritage"
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1652", "4.7/5 (94 reviews)")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {t("1653", "Traditional Dutch Island Life")}
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1654",
                    "Step back in time as you explore Marken, a former island that preserves centuries of Dutch maritime tradition. This picturesque village is famous for its distinctive green wooden houses built on stilts and its rich fishing heritage."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1655",
                    "Walk through narrow streets lined with traditional houses, visit the historic harbor, and learn about the unique culture that has been preserved for generations. The village offers a glimpse into authentic Dutch life away from the modern world."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t(
                    "1656",
                    "Don't miss the opportunity to see locals in traditional costumes, taste fresh fish and local cheese, and capture the timeless beauty of this remarkable village that feels like a living museum."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div className="grid grid-cols-2 gap-4">
                {tourHighlights.map((highlight) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                  >
                    <highlight.icon className="h-8 w-8 text-primary mb-3" />
                    <div className="font-semibold text-white text-sm mb-2">
                      {highlight.title}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {highlight.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl"
                >
                  <img
                    src="/images/tours/Marken.webp"
                    alt={t("1657", "Marken Houses")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl mt-8"
                >
                  <img
                    src="/images/tours/Marken.webp"
                    alt={t("1658", "Traditional Harbor")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl col-span-2"
                >
                  <img
                    src="/images/tours/Marken.webp"
                    alt={t("1659", "Village Streets")}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1660",
                "Everything you need to know about your Marken adventure"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1546", "Quick Details")}
                </h3>

                <div className="space-y-6">
                  {tourDetails.map((detail) => (
                    <motion.div
                      key={detail.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center">
                        <detail.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">
                          {detail.label}
                        </p>
                        <p className="text-gray-300">{detail.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1515", "Tour Highlights")}
                </h3>

                <div className="space-y-4">
                  {tourFeatures.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <span className="text-gray-200">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <Anchor className="h-16 w-16 mx-auto mb-6 opacity-80" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1661", "Ready to Explore Traditional Marken?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1662",
                  "Discover authentic Dutch island life in this charming village where time seems to have stood still. Experience traditional culture and maritime heritage."
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center space-x-2"
                >
                  <Calendar className="h-5 w-5" />
                  <span>{t("1549", "Book This Tour")}</span>
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1519", "Ask Questions")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
